import { Helmet } from 'react-helmet';
import { useLocation } from 'react-router';
import { useSelector } from 'react-redux';
import useLambdaImage from '../../Hooks/useImage';
import { toAbsoluteMediaUrl } from '../../Services/ApiService';
import Navbar from '../../Utils/Navbar/navbar-component';
import Breadcrumb from '../Breadcrumb/breadcrumb-component';
import { getLandingData } from '../../Public/landing-data';
import SystemDataService from '../../Services/SystemDataService';
import ParallaxSection from '../LazyLoading/ParallaxSection';
import { fillSeoTokens } from '../../Services/UtilsService';

function Seo({ title, description, keywords, image, ogType, jsonLd, noIndex, is404, hasEnVersion }) {
  const location = useLocation();

  // 1. Get system data including businessInfo
  const systemData = useSelector((s) => s.systemData || {});
  const { language = 'he', businessInfo = {}, supportedLanguages = [] } = systemData;
  const { website } = businessInfo;
  const twitterLink = SystemDataService.byLabel(systemData.links, 'Twitter');

  // Enabled languages (site.supported.languages via geo-language → Redux).
  // First entry = default language (owns the bare URL); a second entry makes
  // the site bilingual and unlocks the hreflang cluster.
  const langs = supportedLanguages.length ? supportedLanguages : ['he', 'en'];
  const defaultLang = langs[0];
  const altLang = langs.length > 1 ? langs.find((l) => l !== defaultLang) : null;

  // 2. Get Defaults from LandingData
  const landingData = getLandingData(language);
  const defaults = landingData.seo;

  // White-label token fill: SEO data stays generic and inherits the client's
  // identity ({business}/{domain}). See Utils/Seo/seo-tokens-util.
  const fill = (s) => fillSeoTokens(s, {
    businessName: businessInfo.businessName || defaults.brand || '',
    website,
  });

  // 3. Fallbacks
  const finalTitle = fill(title || defaults.title);
  const rawDescription = fill(description || defaults.description || '');
  const finalDescription = rawDescription.length <= 160
    ? rawDescription
    : rawDescription.slice(0, 160).replace(/\s+\S*$/, '') + '…';
  const finalKeywords = fill(keywords || defaults.keywords);
  const imageUsed = image || businessInfo.mainLogo;
  // og:image / twitter:image must be absolute URLs, media paths are site-relative.
  const finalImage = toAbsoluteMediaUrl(useLambdaImage(imageUsed, 400, 40));

  // 4. Language-aware URLs
  // Default-language canonical = bare pathname; alternate = pathname + ?lan=<alt>
  const cleanBaseUrl = website?.replace(/\/$/, '') || '';
  const bareUrl = `${cleanBaseUrl}${location.pathname}`;
  const altUrl  = altLang ? `${bareUrl}?lan=${altLang}` : bareUrl;
  const canonicalUrl = language === defaultLang ? bareUrl : altUrl;

  // Only emit hreflang when the site actually serves two languages, the page
  // is translated, and it's indexable, never point crawlers at a disabled language.
  const emitHreflang = !!altLang && hasEnVersion && !noIndex;

  return (
    <Helmet>
      {/* --- HTML document language + direction --- */}
      <html lang={language === 'he' ? 'he' : 'en'} dir={language === 'he' ? 'rtl' : 'ltr'} />

      {/* --- Basic Metadata --- */}
      <title>{finalTitle}</title>
      {!noIndex && <link rel="canonical" href={canonicalUrl} />}

      {/* --- hreflang (bidirectional cluster, same on both language versions) --- */}
      {emitHreflang && <link rel="alternate" hreflang={defaultLang} href={bareUrl} />}
      {emitHreflang && <link rel="alternate" hreflang={altLang}     href={altUrl}  />}
      {emitHreflang && <link rel="alternate" hreflang="x-default"   href={bareUrl} />}

      <meta name="description" key="description" content={finalDescription} />
      <meta name="keywords" key="keywords" content={finalKeywords} />

      {/* --- Open Graph (Facebook, LinkedIn, etc.) --- */}
      <meta property="og:url"         key="og:url"         content={canonicalUrl} />
      <meta property="og:type"        key="og:type"        content={ogType || 'website'} />
      <meta property="og:title"       key="og:title"       content={finalTitle} />
      <meta property="og:description" key="og:description" content={finalDescription} />
      <meta property="og:image"       key="og:image"       content={finalImage} />

      {/* --- X (Twitter) Cards --- */}
      <meta name="twitter:card"        key="twitter:card"        content="summary_large_image" />
      <meta name="twitter:title"       key="twitter:title"       content={finalTitle} />
      <meta name="twitter:description" key="twitter:description" content={finalDescription} />
      <meta name="twitter:image"       key="twitter:image"       content={finalImage} />

      {twitterLink?.description && <meta name="twitter:site" key="twitter:site" content={twitterLink?.description} />}

      {/* --- AI Content Summary --- */}
      <meta name="ai-content-summary" content={finalDescription} />

      {/* --- Robots --- */}
      <meta name="robots" content={noIndex ? 'noindex, nofollow' : 'index, follow'} />
      {is404 && <meta name="prerender-status-code" content="404" />}

      {/* --- Structured Data (JSON-LD) --- */}
      {jsonLd && jsonLd.map((schema, i) => (
        <script key={i} type="application/ld+json">
          {JSON.stringify(schema)}
        </script>
      ))}
    </Helmet>
  );
}

function Layout({ children, seo = {}, overlayHeader = false, crumbs = null }) {
  // overlayHeader: the first child (a hero) draws its own top padding, so the
  // fixed navbar can float transparently over it. Otherwise reserve navbar space.
  return (
    <>
      <Seo
        title={seo.title}
        description={seo.description}
        keywords={seo.keywords}
        image={seo.image}
        ogType={seo.ogType}
        jsonLd={seo.jsonLd}
        noIndex={seo.noIndex}
        is404={seo.is404}
        hasEnVersion={seo.hasEnVersion}
      />
      <Navbar overlay={overlayHeader} />
      {/* Breadcrumb lives OUTSIDE ParallaxSection so it stays flush under the fixed
          navbar, the parallax drifts page content, and a breadcrumb caught in it
          would float up under the navbar. */}
      {crumbs?.length ? <Breadcrumb crumbs={crumbs} /> : null}
      <ParallaxSection speed={0.95}>
        {/* Non-overlay pages: the navbar is sticky and sits in normal flow, so it
            reserves its own height, no top padding needed here. Overlay pages have
            a fixed navbar floating over a hero that draws its own top padding. */}
        <main className="min-h-screen">
          {children}
        </main>
      </ParallaxSection>
    </>
  );
}

export default Layout;